import { useState, useEffect } from "react";
import axios from "axios";
import LikersDialog from "@/components/LikersDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const REACTIONS = [
  { key: "like",  emoji: "👍", label: "Curtir",  color: "text-blue-500" },
  { key: "love",  emoji: "❤️", label: "Amei",    color: "text-red-500" },
  { key: "haha",  emoji: "😂", label: "Haha",    color: "text-amber-500" },
  { key: "wow",   emoji: "😮", label: "Uau",     color: "text-amber-500" },
  { key: "sad",   emoji: "😢", label: "Triste",  color: "text-sky-500" },
  { key: "angry", emoji: "😡", label: "Grr",     color: "text-orange-600" },
];
const BY_KEY = Object.fromEntries(REACTIONS.map((r) => [r.key, r]));

/**
 * Reaction picker for posts. Long-press or hover the button to see all 6 emojis.
 * Click the button to toggle the current emoji (defaults to like).
 */
export default function PostReactions({ postId, initialReactions, initialMy, initialTotal, compact = false, onChange }) {
  const [reactions, setReactions] = useState(initialReactions || {});
  const [my, setMy] = useState(initialMy || null);
  const [total, setTotal] = useState(initialTotal ?? 0);
  const [open, setOpen] = useState(false);
  const [likersOpen, setLikersOpen] = useState(false);

  useEffect(() => {
    // If parent didn't preload, fetch once
    if (initialReactions !== undefined) return;
    axios.get(`${API}/posts/${postId}/reactions`).then((r) => {
      setReactions(r.data.reactions || {});
      setMy(r.data.my_reaction || null);
      setTotal(r.data.total || 0);
    }).catch(() => {});
  }, [postId, initialReactions]);

  const react = async (emoji) => {
    setOpen(false);
    try {
      const r = await axios.post(`${API}/posts/${postId}/react`, { emoji });
      setReactions(r.data.reactions || {});
      setMy(r.data.my_reaction || null);
      setTotal(r.data.total || 0);
      onChange?.(r.data);
    } catch { /* empty */ }
  };

  const topEmojis = Object.entries(reactions).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([k]) => BY_KEY[k]?.emoji).filter(Boolean);
  const myMeta = my ? BY_KEY[my] : BY_KEY.like;
  const label = my ? BY_KEY[my]?.label : "Curtir";

  return (
    <div className="relative inline-flex items-center gap-2">
      <button
        data-testid={`react-btn-${postId}`}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onClick={() => react(my || "like")}
        className={`flex items-center gap-1 hover:text-primary transition-colors ${my ? myMeta.color + " font-semibold" : ""}`}
      >
        <span className="text-lg leading-none">{myMeta?.emoji || "👍"}</span>
        {!compact && <span className="text-sm">{label}</span>}
      </button>
      {total > 0 && (
        <button
          type="button"
          data-testid={`likers-btn-${postId}`}
          onClick={() => setLikersOpen(true)}
          title="Ver quem curtiu"
          className="text-sm flex items-center gap-0.5 hover:underline"
        >
          {topEmojis.map((e, i) => <span key={i} className="-ml-1 text-xs">{e}</span>)}
          <b className="ml-1">{total}</b>
        </button>
      )}
      <LikersDialog postId={postId} open={likersOpen} onOpenChange={setLikersOpen} />
      {open && (
        <div
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
          className="absolute -top-12 left-0 z-50 flex gap-1 px-2 py-1 rounded-full border shadow-lg bg-background"
          data-testid={`react-picker-${postId}`}
        >
          {REACTIONS.map((r) => (
            <button
              key={r.key}
              onClick={() => react(r.key)}
              title={r.label}
              data-testid={`react-${r.key}-${postId}`}
              className={`w-9 h-9 flex items-center justify-center text-xl rounded-full hover:scale-125 hover:-translate-y-1 transition-transform ${my === r.key ? "bg-accent" : ""}`}
            >
              {r.emoji}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
